import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatTeardropDots, Briefcase, Users, Envelope } from '@phosphor-icons/react';
import { MessagingView } from '@/components/messaging/MessagingView';
import { fetchThreads, type ThreadKind } from '@/lib/messagingApi';
import { useAuthStore } from '@/features/auth/store';
import { cn } from '@/lib/utils';
import { useFillViewportHeight } from '@/hooks/useFillViewportHeight';

type KindFilter = ThreadKind | null;

const KIND_CHIPS: { key: KindFilter; label: string; Icon: React.ElementType }[] = [
  { key: null,                label: 'All',        Icon: ChatTeardropDots },
  { key: 'matter_internal',   label: 'Matters',    Icon: Briefcase        },
  { key: 'matter_external',   label: 'Solicitors', Icon: Envelope         },
  { key: 'chambers_internal', label: 'Chambers',   Icon: Users            },
];

export default function ClerkCommunicationsPage() {
  const currentUser = useAuthStore((s) => s.user);
  const [kindFilter, setKindFilter] = useState<KindFilter>(null);
  // The panel runs from wherever it ends up down to the bottom of the screen. It used to be
  // `calc(100dvh - 148px)`, a number counted against one layout — and the strip above now wraps
  // onto two or three lines on a phone, which would have pushed the bottom of the conversation
  // list below the fold.
  const [paneRef, paneHeight] = useFillViewportHeight();

  const { data: allThreads = [], isError: countsFailed, refetch: refetchCounts } = useQuery({
    queryKey: ['messaging', 'threads', 'clerk-stats'],
    queryFn: () => fetchThreads({ limit: 100 }),
    enabled: !!currentUser,
    staleTime: 30_000,
    refetchInterval: 120_000,
  });

  // A failed fetch leaves `allThreads` empty, and every counter below then read a confident
  // "0 unread · 0 matters · 0 solicitors · 0 internal" — an all-clear nobody had verified, on the
  // screen a clerk uses to decide whether anything needs answering. An em dash says "not known".
  const dash = (n: number) => (countsFailed ? '\u2014' : String(n));
  const totalUnread     = allThreads.reduce((s, t) => s + (t.unreadCount ?? 0), 0);
  const matterUnread    = allThreads.filter(t => t.kind === 'matter_internal'  && (t.unreadCount ?? 0) > 0).length;
  const solicitorUnread = allThreads.filter(t => t.kind === 'matter_external'  && (t.unreadCount ?? 0) > 0).length;
  const internalUnread  = allThreads.filter(t => t.kind === 'chambers_internal' && (t.unreadCount ?? 0) > 0).length;

  return (
    <div className="flex flex-col gap-2">
      {/* The strip is four groups — the label, the unread counts, the kind chips and the dividers
          between them — and it could not wrap, so on a phone it ran off the right edge: the
          chips were a whole screen-width past it and the counts were sliced. It wraps onto as
          many lines as it needs now, and is unchanged from the moment the row fits on one. The
          dividers only earn their place while it IS one line; stacked, they are a stray tick. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-4 py-2.5">
        <span className="text-sm font-semibold text-foreground">Communications</span>

        <div className="mx-1 hidden h-4 w-px bg-border sm:block" />

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className={cn('flex items-center gap-1', !countsFailed && totalUnread > 0 ? 'font-bold text-brand-700 dark:text-brand-300' : 'text-muted-foreground')}>
            <ChatTeardropDots className="h-3 w-3" />{dash(totalUnread)} unread
          </span>
          <span className={cn('flex items-center gap-1', !countsFailed && matterUnread > 0 ? 'font-semibold text-brand-600 dark:text-brand-400' : 'text-muted-foreground')}>
            <Briefcase className="h-3 w-3" />{dash(matterUnread)} matters
          </span>
          <span className={cn('flex items-center gap-1', !countsFailed && solicitorUnread > 0 ? 'font-semibold text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}>
            <Envelope className="h-3 w-3" />{dash(solicitorUnread)} solicitors
          </span>
          <span className={cn('flex items-center gap-1', !countsFailed && internalUnread > 0 ? 'font-semibold text-brand-600 dark:text-brand-400' : 'text-muted-foreground')}>
            <Users className="h-3 w-3" />{dash(internalUnread)} internal
          </span>
        </div>

        {countsFailed && (
          <button
            type="button"
            onClick={() => void refetchCounts()}
            className="text-xs font-medium text-amber-700 underline dark:text-amber-300"
          >
            Couldn't load the unread counts — try again
          </button>
        )}

        <div className="mx-1 hidden h-4 w-px bg-border sm:block" />

        <div className="flex flex-wrap items-center gap-1">
          {KIND_CHIPS.map(({ key, label, Icon }) => (
            <button
              key={String(key)}
              type="button"
              onClick={() => setKindFilter(key)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium transition',
                kindFilter === key
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-border bg-muted/40 text-muted-foreground hover:border-brand-300 hover:text-foreground',
              )}
            >
              <Icon className="h-3 w-3" />{label}
            </button>
          ))}
        </div>

      </div>

      <div ref={paneRef}>
        <MessagingView
          kindFilter={kindFilter}
          defaultKind="chambers_internal"
          height={paneHeight}
        />
      </div>
    </div>
  );
}
